import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
} from 'reactstrap';
import { toast } from 'react-toastify';
import classnames from 'classnames';
import {
  dailyRecordsAPI,
  projectsAPI,
  personnelAPI,
  vehiclesAPI,
  workItemsAPI,
} from '../../helpers/api';
import { useIsForman } from '../../helpers/hooks';
import { useIsMobile } from '../../helpers/useMediaQuery';

const DailyRecordForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isForman = useIsForman();
  const isMobile = useIsMobile();
  const isEdit = !!id;

  const [activeTab, setActiveTab] = useState('1');
  const [submitting, setSubmitting] = useState(false);

  // Form verileri
  const [formData, setFormData] = useState({
    recordDate: new Date().toISOString().split('T')[0],
    siteId: '',
    notes: '',
  });

  // Seçenekler
  const [sites, setSites] = useState<any[]>([]);
  const [allPersonnel, setAllPersonnel] = useState<any[]>([]);
  const [allVehicles, setAllVehicles] = useState<any[]>([]);
  const [workItems, setWorkItems] = useState<any[]>([]);

  // Seçimler
  const [selectedPersonnel, setSelectedPersonnel] = useState<
    Array<{ personnelId: string; dailyWage: number }>
  >([]);
  const [selectedVehicles, setSelectedVehicles] = useState<
    Array<{ vehicleId: string; usageType: string; notes: string }>
  >([]);
  const [selectedWorkItems, setSelectedWorkItems] = useState<
    Array<{ workItemId: string; quantity: number; notes: string }>
  >([]);

  useEffect(() => {
    fetchSites();
    fetchPersonnel();
    fetchVehicles();
  }, []);

  useEffect(() => {
    if (formData.siteId) {
      // Şantiye seçildiğinde, projenin iş kalemlerini getir
      const site = sites.find((s) => s.id === formData.siteId);
      if (site) {
        fetchWorkItems(site.projectId);
      }
    }
  }, [formData.siteId, sites]);

  const fetchSites = async () => {
    try {
      const response = await projectsAPI.getSites();
      setSites(response.data);
    } catch (error) {
      console.error('Sites fetch error:', error);
    }
  };

  const fetchPersonnel = async () => {
    try {
      const response = await personnelAPI.getAll();
      setAllPersonnel(response.data);
    } catch (error) {
      console.error('Personnel fetch error:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await vehiclesAPI.getAll();
      setAllVehicles(response.data);
    } catch (error) {
      console.error('Vehicles fetch error:', error);
    }
  };

  const fetchWorkItems = async (projectId: string) => {
    try {
      const response = await workItemsAPI.getAll(projectId);
      setWorkItems(response.data);
    } catch (error) {
      console.error('Work items fetch error:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.siteId) {
      toast.error('Lütfen şantiye seçin');
      return;
    }

    try {
      setSubmitting(true);
      await dailyRecordsAPI.create({
        recordDate: new Date(formData.recordDate),
        siteId: formData.siteId,
        notes: formData.notes || undefined,
        personnelEntries: selectedPersonnel,
        vehicleEntries: selectedVehicles,
        workEntries: selectedWorkItems,
      });

      toast.success('Günlük kayıt oluşturuldu');
      navigate(isForman ? '/forman' : '/daily-records');
    } catch (error: any) {
      if (error.response?.data?.missingDays) {
        toast.error(
          `Eksik günler var: ${error.response.data.missingDays.join(', ')}`
        );
      } else {
        toast.error(error.response?.data?.message || 'Kayıt başarısız');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const addPersonnel = (personnelId: string) => {
    if (selectedPersonnel.find((p) => p.personnelId === personnelId)) return;
    setSelectedPersonnel([...selectedPersonnel, { personnelId, dailyWage: 1 }]);
  };

  const removePersonnel = (personnelId: string) => {
    setSelectedPersonnel(selectedPersonnel.filter((p) => p.personnelId !== personnelId));
  };

  const updatePersonnelWage = (personnelId: string, dailyWage: number) => {
    setSelectedPersonnel(
      selectedPersonnel.map((p) =>
        p.personnelId === personnelId ? { ...p, dailyWage } : p
      )
    );
  };

  // Araç işlemleri
  const addVehicle = (vehicleId: string) => {
    if (selectedVehicles.find((v) => v.vehicleId === vehicleId)) return;
    setSelectedVehicles([
      ...selectedVehicles,
      { vehicleId, usageType: 'FULL_DAY', notes: '' },
    ]);
  };

  const removeVehicle = (vehicleId: string) => {
    setSelectedVehicles(selectedVehicles.filter((v) => v.vehicleId !== vehicleId));
  };

  const updateVehicle = (vehicleId: string, field: string, value: string) => {
    setSelectedVehicles(
      selectedVehicles.map((v) =>
        v.vehicleId === vehicleId ? { ...v, [field]: value } : v
      )
    );
  };

  // İş kalemi işlemleri
  const addWorkItem = (workItemId: string) => {
    if (selectedWorkItems.find((w) => w.workItemId === workItemId)) return;
    setSelectedWorkItems([
      ...selectedWorkItems,
      { workItemId, quantity: 0, notes: '' },
    ]);
  };

  const removeWorkItem = (workItemId: string) => {
    setSelectedWorkItems(selectedWorkItems.filter((w) => w.workItemId !== workItemId));
  };

  const updateWorkItem = (workItemId: string, field: string, value: string | number) => {
    setSelectedWorkItems(
      selectedWorkItems.map((w) =>
        w.workItemId === workItemId ? { ...w, [field]: value } : w
      )
    );
  };

  const backPath = isForman ? '/forman' : '/daily-records';

  return (
    <>
      {isForman ? (
        <div className="forman-page-header">
          <button className="back-btn" onClick={() => navigate(backPath)}>
            <i className="ri-arrow-left-line"></i>
          </button>
          <h1>{isEdit ? 'Günlük Kayıt Düzenle' : 'Günlük Kayıt'}</h1>
        </div>
      ) : (
        <Row>
          <Col xs={12}>
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0">
                {isEdit ? 'Günlük Kayıt Düzenle' : 'Yeni Günlük Kayıt'}
              </h4>
            </div>
          </Col>
        </Row>
      )}

      <Form onSubmit={handleSubmit}>
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">Temel Bilgiler</h5>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Tarih *</Label>
                        <Input
                          type="date"
                          value={formData.recordDate}
                          onChange={(e) =>
                            setFormData({ ...formData, recordDate: e.target.value })
                          }
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Şantiye *</Label>
                        <Input
                          type="select"
                          value={formData.siteId}
                          onChange={(e) =>
                            setFormData({ ...formData, siteId: e.target.value })
                          }
                          required
                        >
                          <option value="">Şantiye Seçin</option>
                          {sites.map((site) => (
                            <option key={site.id} value={site.id}>
                              {site.project.city.name} / {site.project.name} / {site.name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Not</Label>
                        <Input
                          type="text"
                          value={formData.notes}
                          onChange={(e) =>
                            setFormData({ ...formData, notes: e.target.value })
                          }
                          placeholder="Genel not..."
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Personel, Araç, İş Sekmeleri */}
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Nav tabs className="nav-tabs-custom nav-primary" style={isMobile ? { overflowX: 'auto', flexWrap: 'nowrap' } : {}}>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '1' })}
                        onClick={() => setActiveTab('1')}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className="ri-team-line me-1"></i>
                        Personel ({selectedPersonnel.length})
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '2' })}
                        onClick={() => setActiveTab('2')}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className="ri-truck-line me-1"></i>
                        Araçlar ({selectedVehicles.length})
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '3' })}
                        onClick={() => setActiveTab('3')}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className="ri-hammer-line me-1"></i>
                        İşler ({selectedWorkItems.length})
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={activeTab} className="pt-4">
                    {/* Personel Sekmesi */}
                    <TabPane tabId="1">
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label>Personel Ekle</Label>
                            <Input
                              type="select"
                              onChange={(e) => {
                                if (e.target.value) addPersonnel(e.target.value);
                                e.target.value = '';
                              }}
                            >
                              <option value="">Personel Seçin...</option>
                              {allPersonnel
                                .filter(
                                  (p) =>
                                    !selectedPersonnel.find(
                                      (sp) => sp.personnelId === p.id
                                    )
                                )
                                .map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.firstName} {p.lastName} - {p.title || 'İşçi'}
                                  </option>
                                ))}
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>

                      {selectedPersonnel.length > 0 && (
                        <>
                          {isMobile ? (
                            <div className="admin-card-list mt-3">
                              {selectedPersonnel.map((sp) => {
                                const person = allPersonnel.find(
                                  (p) => p.id === sp.personnelId
                                );
                                return (
                                  <div key={sp.personnelId} className="admin-card-item">
                                    <div className="admin-card-header">
                                      <h6 className="admin-card-title mb-0">
                                        {person?.firstName} {person?.lastName}
                                      </h6>
                                      <span className="badge bg-primary">
                                        Yevmiye: {sp.dailyWage}
                                      </span>
                                    </div>
                                    <div className="admin-card-body">
                                      <div className="admin-info-row">
                                        <span className="admin-info-label">Yevmiye Seçimi:</span>
                                        <Input
                                          type="select"
                                          value={sp.dailyWage}
                                          onChange={(e) =>
                                            updatePersonnelWage(
                                              sp.personnelId,
                                              parseFloat(e.target.value)
                                            )
                                          }
                                        >
                                          <option value={0.5}>0.5</option>
                                          <option value={1}>1</option>
                                          <option value={1.5}>1.5</option>
                                          <option value={2}>2</option>
                                          <option value={2.5}>2.5</option>
                                          <option value={3}>3</option>
                                        </Input>
                                      </div>
                                    </div>
                                    <div className="admin-card-footer">
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => removePersonnel(sp.personnelId)}
                                      >
                                        <i className="ri-delete-bin-line"></i> Kaldır
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <Table className="mt-3">
                              <thead>
                                <tr>
                                  <th>Personel</th>
                                  <th style={{ width: '150px' }}>Yevmiye</th>
                                  <th style={{ width: '100px' }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedPersonnel.map((sp) => {
                                  const person = allPersonnel.find(
                                    (p) => p.id === sp.personnelId
                                  );
                                  return (
                                    <tr key={sp.personnelId}>
                                      <td>
                                        {person?.firstName} {person?.lastName}
                                      </td>
                                      <td>
                                        <Input
                                          type="select"
                                          value={sp.dailyWage}
                                          onChange={(e) =>
                                            updatePersonnelWage(
                                              sp.personnelId,
                                              parseFloat(e.target.value)
                                            )
                                          }
                                        >
                                          <option value={0.5}>0.5</option>
                                          <option value={1}>1</option>
                                          <option value={1.5}>1.5</option>
                                          <option value={2}>2</option>
                                          <option value={2.5}>2.5</option>
                                          <option value={3}>3</option>
                                        </Input>
                                      </td>
                                      <td>
                                        <Button
                                          color="danger"
                                          size="sm"
                                          onClick={() => removePersonnel(sp.personnelId)}
                                        >
                                          <i className="ri-delete-bin-line"></i>
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          )}
                        </>
                      )}
                    </TabPane>

                    {/* Araç Sekmesi */}
                    <TabPane tabId="2">
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label>Araç Ekle</Label>
                            <Input
                              type="select"
                              onChange={(e) => {
                                if (e.target.value) addVehicle(e.target.value);
                                e.target.value = '';
                              }}
                            >
                              <option value="">Araç Seçin...</option>
                              {allVehicles
                                .filter(
                                  (v) =>
                                    v.isActive &&
                                    !selectedVehicles.find((sv) => sv.vehicleId === v.id)
                                )
                                .map((v) => (
                                  <option key={v.id} value={v.id}>
                                    {v.plate} - {v.brand || ''} {v.model || ''}
                                  </option>
                                ))}
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>

                      {selectedVehicles.length > 0 && (
                        <>
                          {isMobile ? (
                            <div className="admin-card-list mt-3">
                              {selectedVehicles.map((sv) => {
                                const vehicle = allVehicles.find((v) => v.id === sv.vehicleId);
                                const usageTypeLabel = sv.usageType === 'FULL_DAY' ? 'Tam Gün' : sv.usageType === 'HALF_DAY' ? 'Yarım Gün' : 'Saatlik';
                                return (
                                  <div key={sv.vehicleId} className="admin-card-item">
                                    <div className="admin-card-header">
                                      <h6 className="admin-card-title mb-0">
                                        {vehicle?.plate}
                                      </h6>
                                      <span className="badge bg-primary">
                                        {usageTypeLabel}
                                      </span>
                                    </div>
                                    <div className="admin-card-body">
                                      <div className="admin-info-row">
                                        <span className="admin-info-label">Araç:</span>
                                        <span className="admin-info-value">
                                          {vehicle?.brand} {vehicle?.model}
                                        </span>
                                      </div>
                                      <div className="admin-info-row">
                                        <span className="admin-info-label">Kullanım Tipi:</span>
                                        <Input
                                          type="select"
                                          value={sv.usageType}
                                          onChange={(e) =>
                                            updateVehicle(sv.vehicleId, 'usageType', e.target.value)
                                          }
                                        >
                                          <option value="FULL_DAY">Tam Gün</option>
                                          <option value="HALF_DAY">Yarım Gün</option>
                                          <option value="HOURLY">Saatlik</option>
                                        </Input>
                                      </div>
                                      <div className="admin-info-row">
                                        <span className="admin-info-label">Not:</span>
                                        <Input
                                          type="text"
                                          placeholder="Not..."
                                          value={sv.notes}
                                          onChange={(e) =>
                                            updateVehicle(sv.vehicleId, 'notes', e.target.value)
                                          }
                                        />
                                      </div>
                                    </div>
                                    <div className="admin-card-footer">
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => removeVehicle(sv.vehicleId)}
                                      >
                                        <i className="ri-delete-bin-line"></i> Kaldır
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <Table className="mt-3">
                              <thead>
                                <tr>
                                  <th>Araç</th>
                                  <th style={{ width: '150px' }}>Kullanım</th>
                                  <th style={{ width: '200px' }}>Not</th>
                                  <th style={{ width: '100px' }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedVehicles.map((sv) => {
                                  const vehicle = allVehicles.find((v) => v.id === sv.vehicleId);
                                  return (
                                    <tr key={sv.vehicleId}>
                                      <td>
                                        <strong>{vehicle?.plate}</strong>
                                        <br />
                                        <small className="text-muted">
                                          {vehicle?.brand} {vehicle?.model}
                                        </small>
                                      </td>
                                      <td>
                                        <Input
                                          type="select"
                                          value={sv.usageType}
                                          onChange={(e) =>
                                            updateVehicle(sv.vehicleId, 'usageType', e.target.value)
                                          }
                                        >
                                          <option value="FULL_DAY">Tam Gün</option>
                                          <option value="HALF_DAY">Yarım Gün</option>
                                          <option value="HOURLY">Saatlik</option>
                                        </Input>
                                      </td>
                                      <td>
                                        <Input
                                          type="text"
                                          placeholder="Not..."
                                          value={sv.notes}
                                          onChange={(e) =>
                                            updateVehicle(sv.vehicleId, 'notes', e.target.value)
                                          }
                                        />
                                      </td>
                                      <td>
                                        <Button
                                          color="danger"
                                          size="sm"
                                          onClick={() => removeVehicle(sv.vehicleId)}
                                        >
                                          <i className="ri-delete-bin-line"></i>
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          )}
                        </>
                      )}
                    </TabPane>

                    {/* İş Sekmesi */}
                    <TabPane tabId="3">
                      {!formData.siteId ? (
                        <div className="text-center py-4 text-muted">
                          İş kalemi eklemek için önce şantiye seçin.
                        </div>
                      ) : workItems.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                          Bu projeye ait iş kalemi bulunamadı.
                        </div>
                      ) : (
                        <>
                          <Row>
                            <Col md={4}>
                              <FormGroup>
                                <Label>İş Kalemi Ekle</Label>
                                <Input
                                  type="select"
                                  onChange={(e) => {
                                    if (e.target.value) addWorkItem(e.target.value);
                                    e.target.value = '';
                                  }}
                                >
                                  <option value="">İş Kalemi Seçin...</option>
                                  {workItems
                                    .filter(
                                      (w) =>
                                        !selectedWorkItems.find((sw) => sw.workItemId === w.id)
                                    )
                                    .map((w) => (
                                      <option key={w.id} value={w.id}>
                                        {w.name} ({w.unit})
                                      </option>
                                    ))}
                                </Input>
                              </FormGroup>
                            </Col>
                          </Row>

                          {selectedWorkItems.length > 0 && (
                            <>
                              {isMobile ? (
                                <div className="admin-card-list mt-3">
                                  {selectedWorkItems.map((sw) => {
                                    const workItem = workItems.find((w) => w.id === sw.workItemId);
                                    return (
                                      <div key={sw.workItemId} className="admin-card-item">
                                        <div className="admin-card-header">
                                          <h6 className="admin-card-title mb-0">
                                            {workItem?.name}
                                          </h6>
                                          <span className="badge bg-secondary">
                                            {workItem?.unit}
                                          </span>
                                        </div>
                                        <div className="admin-card-body">
                                          <div className="admin-info-row">
                                            <span className="admin-info-label">Miktar:</span>
                                            <Input
                                              type="number"
                                              min={0}
                                              step={0.01}
                                              value={sw.quantity}
                                              onChange={(e) =>
                                                updateWorkItem(
                                                  sw.workItemId,
                                                  'quantity',
                                                  parseFloat(e.target.value) || 0
                                                )
                                              }
                                            />
                                          </div>
                                          <div className="admin-info-row">
                                            <span className="admin-info-label">Not:</span>
                                            <Input
                                              type="text"
                                              placeholder="Not..."
                                              value={sw.notes}
                                              onChange={(e) =>
                                                updateWorkItem(sw.workItemId, 'notes', e.target.value)
                                              }
                                            />
                                          </div>
                                        </div>
                                        <div className="admin-card-footer">
                                          <Button
                                            color="danger"
                                            size="sm"
                                            onClick={() => removeWorkItem(sw.workItemId)}
                                          >
                                            <i className="ri-delete-bin-line"></i> Kaldır
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <Table className="mt-3">
                                  <thead>
                                    <tr>
                                      <th>İş Kalemi</th>
                                      <th style={{ width: '120px' }}>Miktar</th>
                                      <th style={{ width: '80px' }}>Birim</th>
                                      <th style={{ width: '200px' }}>Not</th>
                                      <th style={{ width: '100px' }}></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {selectedWorkItems.map((sw) => {
                                      const workItem = workItems.find((w) => w.id === sw.workItemId);
                                      return (
                                        <tr key={sw.workItemId}>
                                          <td>
                                            <strong>{workItem?.name}</strong>
                                          </td>
                                          <td>
                                            <Input
                                              type="number"
                                              min={0}
                                              step={0.01}
                                              value={sw.quantity}
                                              onChange={(e) =>
                                                updateWorkItem(
                                                  sw.workItemId,
                                                  'quantity',
                                                  parseFloat(e.target.value) || 0
                                                )
                                              }
                                            />
                                          </td>
                                          <td>
                                            <span className="badge bg-secondary">
                                              {workItem?.unit}
                                            </span>
                                          </td>
                                          <td>
                                            <Input
                                              type="text"
                                              placeholder="Not..."
                                              value={sw.notes}
                                              onChange={(e) =>
                                                updateWorkItem(sw.workItemId, 'notes', e.target.value)
                                              }
                                            />
                                          </td>
                                          <td>
                                            <Button
                                              color="danger"
                                              size="sm"
                                              onClick={() => removeWorkItem(sw.workItemId)}
                                            >
                                              <i className="ri-delete-bin-line"></i>
                                            </Button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Kaydet Butonu */}
          <Row>
            <Col>
              <div className="d-flex gap-2 justify-content-end">
                <Button
                  color="secondary"
                  onClick={() => navigate(backPath)}
                >
                  İptal
                </Button>
                <Button color="primary" type="submit" disabled={submitting}>
                  {submitting ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </div>
            </Col>
          </Row>
      </Form>
    </>
  );
};

export default DailyRecordForm;
